import { useMemo } from "react";

import { useMyAccessCases } from "@/features/access/api";
import { useMe } from "@/lib/api";

import { NAV_FEATURES } from "./nav-features";
import {
  flattenGroups,
  itAdminGroupsFor,
  type NavGroup,
  type NavItem,
  wantsAccessBadge,
} from "./nav-items";

export interface ItAdminNav {
  /** The visible groups, empty ones dropped; empty = no "IT Admin" at all. */
  groups: NavGroup[];
  /** The same items, flat, in menu order. */
  items: NavItem[];
  /**
   * Pending actions for the badge: the access cases with a step for the
   * reader (`/access/mine` → `action_count`). 0 while unknown.
   */
  pending: number;
}

/**
 * The "IT Admin" menu for the signed-in person, shared by the desktop
 * dropdown and the mobile drawer so the two can never disagree.
 *
 * The badge reuses `/access/mine`, the call the Ask page's activity panel and
 * My Tickets already make (same query key, so the cache is shared); it runs
 * only when Access requests are listed, and adds no call of its own.
 */
export function useItAdminNav(): ItAdminNav {
  const { data: me } = useMe();
  const groups = useMemo(() => itAdminGroupsFor(me, NAV_FEATURES), [me]);
  const items = useMemo(() => flattenGroups(groups), [groups]);
  const counted = wantsAccessBadge(groups);
  const mine = useMyAccessCases(counted);
  const pending = counted ? (mine.data?.action_count ?? 0) : 0;
  return { groups, items, pending };
}
